import { useEffect, useCallback } from 'react';

// Preload critical resources
//
// Historically this hook injected <link rel="preload" as="style"> for
// `/src/index.css` and the Google Fonts stylesheet, but Vite already bundles
// the CSS to a hashed `/assets/...` URL and the browser counts the bare
// `/src/index.css` preload as unused — producing dozens of "preloaded using
// link preload but not used within a few seconds" warnings on every page
// load (and an actual second network request in dev). The warnings turned
// into noise that masked real perf issues, so the CSS/font preload calls are
// removed. Only resources that are *actually* shown in the first paint are
// preloaded here.
export function usePreloadResources() {
  const preloadResource = useCallback((href: string, as: string) => {
    if (typeof document === 'undefined') return;
    if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (as === 'image') link.fetchPriority = 'high';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    // Logo is shown in the top bar on first paint; preloading it gives a
    // visible win without spamming the console.
    preloadResource('https://irelandtt.com/img/Irelandtt-logo-transparent.png', 'image');
  }, [preloadResource]);
}

// Preload routes on hover
export function useRoutePreloading() {
  const preloadRoute = useCallback((routePath: string) => {
    // This will trigger the lazy loading of the component
    // The component will be cached by the browser
    import(`../pages/${routePath}.tsx`).catch(() => {
      // Silently fail if route doesn't exist
    });
  }, []);

  const preloadComponent = useCallback((componentPath: string) => {
    import(`../components/${componentPath}.tsx`).catch(() => {
      // Silently fail if component doesn't exist
    });
  }, []);

  return { preloadRoute, preloadComponent };
}

// Preload data for frequently accessed sections
export function useDataPreloading() {
  const preloadData = useCallback(async (dataType: string) => {
    try {
      switch (dataType) {
        case 'customers':
          {
            const { supabaseCustomerStorage } = await import('../services/supabaseStorage');
            await supabaseCustomerStorage.getCustomersList(80, 0);
          }
          break;
        case 'bookings':
          // Preload booking data
          const { supabaseBookingStorage } = await import('../services/supabaseStorage');
          await supabaseBookingStorage.getBookings(50, 0);
          break;
        case 'reminders':
          // Preload reminder data
          const { supabaseReminderStorage } = await import('../services/supabaseStorage');
          await supabaseReminderStorage.getReminders();
          break;
      }
    } catch (error) {
      // Silently fail - this is just preloading
      console.debug('Preload failed for', dataType, error);
    }
  }, []);

  return { preloadData };
}

// Hook to preload based on user behavior
export function useSmartPreloading() {
  const { preloadRoute, preloadComponent } = useRoutePreloading();
  const { preloadData } = useDataPreloading();

  const preloadBasedOnView = useCallback((view: string) => {
    // Defer background preloads so initial dashboard/layout requests are not doubled.
    const runDeferred = (fn: () => void) => {
      window.setTimeout(fn, 3000);
    };

    switch (view) {
      case 'dashboard':
        runDeferred(() => {
          preloadData('customers');
          preloadData('bookings');
        });
        break;
      case 'clients':
        runDeferred(() => {
          preloadComponent('crm/ModernCustomerDetail');
          preloadComponent('crm/AddEditCustomerModal');
          preloadData('customers');
        });
        break;
      case 'bookings':
        runDeferred(() => {
          preloadComponent('bookings/BookingFormModal');
          preloadComponent('bookings/CompanySelectionModal');
          preloadData('bookings');
          preloadData('customers');
        });
        break;
      case 'reminders':
        runDeferred(() => {
          preloadData('reminders');
          preloadData('customers');
        });
        break;
    }
  }, [preloadComponent, preloadData]);

  return { preloadBasedOnView };
}
